import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
    FileText, 
    Download, 
    Users, 
    Mail,
    AlignLeft,
    Search, 
    Check, 
    Calendar, 
    CreditCard, 
    Receipt, 
    Filter,
    Info,
    Sparkles
} from "lucide-react";
import Swal from "sweetalert2";

// Actions
import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import { getAllEntitesRequest } from "../../../../redux/actions/entitesActions";
import { getAllJobsRequest } from "../../../../redux/actions/Jobactions";

// UI Components
import MultiSelectDropdown from "../../../../Ui/UiJs/MultiSelectDropdown";

// Styles
import "../TraitementStyles/ExportArretePaie.css";

/* ─────────────────────── User Row Component ─────────────────────── */
const UserRow = ({ user, isChecked, onToggle }) => {
    const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
    
    return (
        <div 
            className={`eap-user-row ${isChecked ? "checked" : ""}`} 
            onClick={() => onToggle(user.id)}
        >
            <div className="pcPill eap-user-checkbox">
                {isChecked && <Check size={12} strokeWidth={3} />}
            </div>
            <div className="eap-user-avatar">
                {initials}
            </div>
            <div className="eap-user-info">
                <div className="eap-user-name">{user.firstName} {user.lastName}</div>
                <div className="eap-user-email">{user.email || user.employeeCode}</div>
            </div>
            {user.jobName && <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{user.jobName}</span>}
        </div>
    );
};

/* ─────────────────────── Main Component ─────────────────────── */
const ExportArretePaie = () => {
    const dispatch = useDispatch();

    // Redux State
    const { users } = useSelector(s => s.user || s.users || {});
    const { entites } = useSelector(s => s.entite || {});
    const { jobs } = useSelector(s => s.jobs || {});

    // Local State
    const [searchTerm, setSearchTerm] = useState("");
    const [entiteIds, setEntiteIds] = useState([]);
    const [jobIds, setJobIds] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [recipientEmail, setRecipientEmail] = useState("");
    const [sendDay, setSendDay] = useState("1");
    const [emailBody, setEmailBody] = useState(
        "Bonjour,\n\nVeuillez trouver ci-joint le fichier des arrêtés de paie du mois.\n\nCordialement,"
    );
    
    const [categories, setCategories] = useState({
        conges: true,
        avances: false,
        frais: false
    });

    useEffect(() => {
        dispatch(getAllUsersRequest());
        dispatch(getAllEntitesRequest());
        dispatch(getAllJobsRequest());
    }, [dispatch]);

    // Filtering logic
    const filteredUsers = useMemo(() => {
        let list = Array.isArray(users) ? users : [];

        // Filter by search
        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            list = list.filter(u => 
                `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q) ||
                (u.employeeCode || "").toLowerCase().includes(q)
            );
        }

        // Filter by entite
        if (entiteIds.length > 0) {
            list = list.filter(u => entiteIds.includes(Number(u.entiteId || u.entityId)));
        }

        // Filter by job
        if (jobIds.length > 0) {
            list = list.filter(u => jobIds.includes(Number(u.jobId || u.posteId)));
        }

        return list;
    }, [users, searchTerm, entiteIds, jobIds]);

    const toggleUser = (id) => {
        setSelectedUserIds(prev => 
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const toggleAll = () => {
        const allFilteredIds = filteredUsers.map(u => u.id);
        const areAllSelected = allFilteredIds.every(id => selectedUserIds.includes(id));

        if (areAllSelected) {
            setSelectedUserIds(prev => prev.filter(id => !allFilteredIds.includes(id)));
        } else {
            setSelectedUserIds(prev => Array.from(new Set([...prev, ...allFilteredIds])));
        }
    };

    const toggleCategory = (cat) => {
        setCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
    };

    const handleGenerate = () => {
        if (selectedUserIds.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Attention',
                text: 'Veuillez sélectionner au moins un collaborateur.',
                confirmButtonColor: '#2a5ca9'
            });
            return;
        }

        const selectedCats = Object.keys(categories).filter(k => categories[k]);
        if (selectedCats.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Attention',
                text: 'Veuillez sélectionner au moins une catégorie (Congés, Avances ou Frais).',
                confirmButtonColor: '#2a5ca9'
            });
            return;
        }

        Swal.fire({
            title: 'Génération en cours...',
            html: 'Préparation du fichier Arrêté de Paie',
            timer: 2000,
            timerProgressBar: true,
            didOpen: () => {
                Swal.showLoading();
            }
        }).then(() => {
            Swal.fire({
                icon: 'success',
                title: 'Terminé !',
                text: 'Le fichier Arrêté de Paie a été généré avec succès.',
                confirmButtonColor: '#2a5ca9'
            });
        });
    };

    return (
        <div className="pcPage eap-container">
            <div className="eap-wrap">
                
                {/* ── Header ── */}
                <header className="pcHeader eap-header">
                    <div>
                        <h1>Export des arrêtés</h1>
                    </div>
                    <div className="pcWizStep__introIcon">
                        <FileText size={20} />
                    </div>
                </header>

                <div className="pcCard eap-card">
                    <div className="pcBody eap-body">
                        <section className="eap-schedule-section">
                            <h2 className="pcTitle eap-section-title">
                                <Mail size={16} />
                                Planification de l'envoi
                            </h2>

                            <div className="eap-grid">
                                <div className="pcWizField">
                                    <label>Email du destinataire</label>
                                    <div className="eap-input-with-icon">
                                        <Mail size={14} className="eap-field-icon" />
                                        <input
                                            type="email"
                                            className="pcInput eap-icon-input"
                                            placeholder="destinataire@entreprise.com"
                                            value={recipientEmail}
                                            onChange={e => setRecipientEmail(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="pcWizField">
                                    <label>Jour d'envoi mensuel</label>
                                    <div className="eap-input-with-icon">
                                        <Calendar size={14} className="eap-field-icon" />
                                        <input
                                            type="number"
                                            min="1"
                                            max="31"
                                            className="pcInput eap-icon-input"
                                            value={sendDay}
                                            onChange={e => setSendDay(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pcWizField">
                                <label>Corps de l'email</label>
                                <div className="eap-editor-shell">
                                    <div className="eap-editor-toolbar">
                                        <AlignLeft size={14} />
                                        <span>Message</span>
                                    </div>
                                    <textarea
                                        className="pcInput eap-email-editor"
                                        value={emailBody}
                                        onChange={e => setEmailBody(e.target.value)}
                                        placeholder="Saisir le contenu de l'email..."
                                    />
                                </div>
                            </div>
                        </section>

                        {/* ── Section 1: Population Cible ── */}
                        <section>
                            <h2 className="pcTitle eap-section-title">
                                <Users size={16} />
                                Population Cible
                            </h2>

                            <div className="eap-grid">
                                <div className="pcWizField">
                                    <label>Entités</label>
                                    <MultiSelectDropdown 
                                        items={entites || []} 
                                        value={entiteIds} 
                                        onChange={setEntiteIds}
                                        placeholder="Toutes les entités"
                                        getId={x => Number(x.id)} 
                                        getLabel={x => x.nom || x.name} 
                                    />
                                </div>
                                <div className="pcWizField">
                                    <label>Postes</label>
                                    <MultiSelectDropdown 
                                        items={jobs || []} 
                                        value={jobIds} 
                                        onChange={setJobIds}
                                        placeholder="Tous les postes"
                                        getId={x => Number(x.id)} 
                                        getLabel={x => x.nom || x.name} 
                                    />
                                </div>
                            </div>

                            {/* User Selection Panel */}
                            <div className="eap-users-panel">
                                <div className="eap-users-panel__header">
                                    <div className="eap-users-panel__title">
                                        Liste des collaborateurs ({filteredUsers.length})
                                    </div>
                                    <div className="eap-users-search">
                                        <Search size={14} className="eap-users-search-icon" />
                                        <input 
                                            type="text" 
                                            placeholder="Rechercher..." 
                                            className="pcInput eap-users-search-input"
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="eap-users-list">
                                    {filteredUsers.length === 0 ? (
                                        <div className="eap-users-empty">Aucun collaborateur trouvé</div>
                                    ) : (
                                        <>
                                            <div 
                                                className="eap-user-row" 
                                                style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}
                                                onClick={toggleAll}
                                            >
                                                <div className="pcPill eap-user-checkbox" style={filteredUsers.length > 0 && filteredUsers.every(u => selectedUserIds.includes(u.id)) ? { background: 'var(--admin-accent)', borderColor: 'var(--admin-accent)', color: '#fff' } : {}}>
                                                    {filteredUsers.length > 0 && filteredUsers.every(u => selectedUserIds.includes(u.id)) && <Check size={12} strokeWidth={3} />}
                                                </div>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--admin-primary)" }}>
                                                    Tout sélectionner
                                                </div>
                                            </div>
                                            {filteredUsers.map(u => (
                                                <UserRow 
                                                    key={u.id} 
                                                    user={u} 
                                                    isChecked={selectedUserIds.includes(u.id)} 
                                                    onToggle={toggleUser} 
                                                />
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* ── Section 2: Catégories ── */}
                        <section style={{ marginTop: 32 }}>
                            <h2 className="pcTitle eap-section-title">
                                <Filter size={16} />
                                Éléments de l'arrêté
                            </h2>

                            <div className="eap-checkbox-group">
                                <div 
                                    className={`pcMethodCard eap-check-card ${categories.conges ? "active" : ""}`}
                                    onClick={() => toggleCategory("conges")}
                                >
                                    <div className="pcMethodCard__check"><Check size={12} /></div>
                                    <div className="eap-check-icon"><Calendar size={18} /></div>
                                    <span>Congés</span>
                                    <div className="pcMethodCard__desc">Absences et congés payés</div>
                                </div>

                                <div 
                                    className={`pcMethodCard eap-check-card ${categories.avances ? "active" : ""}`}
                                    onClick={() => toggleCategory("avances")}
                                >
                                    <div className="pcMethodCard__check"><Check size={12} /></div>
                                    <div className="eap-check-icon"><CreditCard size={18} /></div>
                                    <span>Avances</span>
                                    <div className="pcMethodCard__desc">Avances sur salaire et prêts</div>
                                </div>

                                <div 
                                    className={`pcMethodCard eap-check-card ${categories.frais ? "active" : ""}`}
                                    onClick={() => toggleCategory("frais")}
                                >
                                    <div className="pcMethodCard__check"><Check size={12} /></div>
                                    <div className="eap-check-icon"><Receipt size={18} /></div>
                                    <span>Notes de frais</span>
                                    <div className="pcMethodCard__desc">Remboursements et dépenses</div>
                                </div>
                            </div>
                        </section>

                        {/* ── Section 3: Action ── */}
                        <div className="eap-footer">
                            <button 
                                className="pcBtn pcBtn--primary eap-download-btn"
                                onClick={handleGenerate}
                                disabled={selectedUserIds.length === 0}
                            >
                                <Download size={18} />
                                Générer le fichier Arrêté
                            </button>
                            <div className="eap-summary">
                                <Info size={14} className="pcAccent" />
                                {selectedUserIds.length} collaborateur(s) sélectionné(s)
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ textAlign: 'center', marginTop: 24, opacity: 0.3 }}>
                    <Sparkles size={20} color="var(--admin-primary)" />
                </div>
            </div>
        </div>
    );
};

export default ExportArretePaie;
